// MapFragmentStore.js — Cached read + observeDeep subscription for map Y.XmlFragments.

import {extractMapState, mapDocFromFragment} from './schema/mapPmAdapter';

// shared-map-data ticket 03: `meta` only ever holds the shared DTG range now (see mapSchema.js's
// `map_meta` — title/basemapStyleId/etc. moved to per-node mapNode attrs), so there's nothing
// meaningful to default here beyond an empty object; callers needing this preview state's
// basemap/title/etc. must read those off the node's own attrs instead (see
// MapNodeView._refreshChrome, MapPreviewQueueService._runJob).
export const EMPTY_PREVIEW_STATE = {
    meta: {},
    layers: [],
    markings: [],
    views: [],
};

/**
 * @param {import('yjs').XmlFragment|null} fragment
 */
export function readMapPreviewState(fragment) {
    if (!fragment || fragment.length === 0) return EMPTY_PREVIEW_STATE;
    const doc = mapDocFromFragment(fragment);
    return doc ? extractMapState(doc) : EMPTY_PREVIEW_STATE;
}

export class MapFragmentStore {
    /**
     * @param {(nodeId: string) => import('yjs').XmlFragment|null} getFragment
     */
    constructor(getFragment) {
        this._getFragment = getFragment;
        /** @type {Map<string, { state: ReturnType<typeof extractMapState> }>} */
        this._cache = new Map();
        /** @type {Map<string, { listeners: Set<Function>, unobserve: (() => void)|null, retryId: number|null }>} */
        this._subscriptions = new Map();
        this._destroyed = false;
    }

    /**
     * @param {string} nodeId
     * @returns {ReturnType<typeof extractMapState>}
     */
    getState(nodeId) {
        const cached = this._cache.get(nodeId);
        if (cached) return cached.state;

        const fragment = this._getFragment(nodeId);
        // Never cache when the fragment is not yet available — ydoc may mount late.
        if (!fragment) return EMPTY_PREVIEW_STATE;

        const state = readMapPreviewState(fragment);
        this._cache.set(nodeId, {state});
        return state;
    }

    invalidate(nodeId) {
        this._cache.delete(nodeId);
    }

    /**
     * @param {string} nodeId
     * @param {(state: ReturnType<typeof extractMapState>) => void} listener
     * @returns {() => void}
     */
    subscribe(nodeId, listener) {
        if (!nodeId) return () => {};

        let sub = this._subscriptions.get(nodeId);
        if (!sub) {
            sub = {listeners: new Set(), unobserve: null, retryId: null};
            this._subscriptions.set(nodeId, sub);
            this._ensureObserver(nodeId, sub);
        }
        sub.listeners.add(listener);

        return () => {
            sub.listeners.delete(listener);
            if (sub.listeners.size === 0) {
                this._teardownSubscription(nodeId, sub);
                this._subscriptions.delete(nodeId);
            }
        };
    }

    _notify(nodeId, sub) {
        this.invalidate(nodeId);
        const state = this.getState(nodeId);
        sub.listeners.forEach((fn) => {
            try {
                fn(state);
            } catch (err) {
                console.warn('[MapFragmentStore] listener failed', err);
            }
        });
    }

    _ensureObserver(nodeId, sub) {
        if (sub.unobserve || sub.retryId != null) return;

        const attach = () => {
            if (this._destroyed) return;
            const fragment = this._getFragment(nodeId);
            if (!fragment) {
                sub.retryId = requestAnimationFrame(attach);
                return;
            }
            sub.retryId = null;

            const handler = () => {
                if (this._destroyed) return;
                this._notify(nodeId, sub);
            };
            fragment.observeDeep(handler);
            sub.unobserve = () => fragment.unobserveDeep(handler);

            // observeDeep does not fire for pre-existing content — seed listeners once.
            if (sub.listeners.size > 0) {
                this._notify(nodeId, sub);
            }
        };

        attach();
    }

    _teardownSubscription(nodeId, sub) {
        if (sub.retryId != null) {
            cancelAnimationFrame(sub.retryId);
            sub.retryId = null;
        }
        sub.unobserve?.();
        sub.unobserve = null;
        this._cache.delete(nodeId);
    }

    destroy() {
        this._destroyed = true;
        this._subscriptions.forEach((sub, nodeId) => this._teardownSubscription(nodeId, sub));
        this._subscriptions.clear();
        this._cache.clear();
    }
}
